import React from 'react';
import { motion } from 'framer-motion';
import { Users, Building2, Award, Sparkles } from 'lucide-react';

const highlights = [
  {
    icon: Building2,
    title: 'Two Strategic Locations',
    description: 'Manufacturing facilities in Noida (wovens) and Bangalore (knits) for complete garment solutions.',
  },
  {
    icon: Users,
    title: 'Expert Team',
    description: 'Dedicated merchandising, sampling, and production teams working closely from design to delivery.',
  },
  {
    icon: Award,
    title: 'Quality Certified',
    description: 'SEDEX, ICS, GOTS, Oekotex, and NEXT approved facilities ensuring global standards.',
  },
  {
    icon: Sparkles,
    title: 'Complete Solutions',
    description: 'In-house laundry, dyeing, embroidery, and testing facilities for end-to-end manufacturing.',
  },
];

const AboutSection = () => {
  return (
    <section 
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-sans text-sm font-medium tracking-[0.3em] uppercase text-slate-400 mb-4">
              About Us
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-slate-900 mb-6">
              Crafting Excellence<br />
              <span className="text-rose-700">Since 1996</span>
            </h2>
            <div className="space-y-6 font-sans text-lg leading-relaxed text-slate-600">
              <p>
                <strong className="text-slate-900">Strawberry Studio Exports Pvt. Ltd.</strong> is a 
                leading manufacturer and exporter of children's wear and ladies' wear, serving the 
                world's most prestigious fashion brands for over 28 years.
              </p>
              <p>
                Our journey began with ladieswear garments, and we have evolved to become specialists 
                in the complete range of children's wear including babies, girls, and boys. Today, we 
                proudly supply to brands across the UK, Europe, USA, and the Middle East.
              </p>
              <p>
                With a combined monthly capacity of over <strong className="text-slate-900">350,000 pieces</strong> across 
                wovens and knits, we offer comprehensive manufacturing solutions with complete 
                traceability and compliance to international standards.
              </p>
            </div>

            {/* Key Promoters */}
            <div className="mt-10 pt-10 border-t border-slate-100">
              <p className="font-sans text-xs font-medium tracking-[0.2em] uppercase text-slate-400 mb-4">
                Leadership
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-serif text-lg text-slate-900">Mr. Atul A. Ruparelia</p>
                  <p className="font-sans text-sm text-slate-500">Chairman</p>
                </div>
                <div>
                  <p className="font-serif text-lg text-slate-900">Mr. Hemant Ruparelia</p>
                  <p className="font-sans text-sm text-slate-500">Managing Director</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Image + Stats */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="relative"
          >
            {/* Main Image - Company Artwork */}
            <div className="relative aspect-[4/5] overflow-hidden bg-white">
              <img
                src="https://customer-assets.emergentagent.com/job_981a21ab-2b01-463f-b2b0-9581b9124d66/artifacts/ns1fxa4t_SSEPL%20Logo.jpg"
                alt="Strawberry Studio - Crafting Excellence in Kidswear"
                className="w-full h-full object-contain p-4 pb-28"
              />
              {/* Overlay Card */}
              <div className="absolute bottom-0 left-0 right-0 bg-slate-900/90 backdrop-blur-sm p-6 md:p-8">
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <p className="font-serif text-2xl md:text-3xl font-medium text-white">75K+</p>
                    <p className="font-sans text-xs text-white/60 uppercase tracking-wider mt-1">Sq.ft Factory</p>
                  </div>
                  <div>
                    <p className="font-serif text-2xl md:text-3xl font-medium text-white">800+</p>
                    <p className="font-sans text-xs text-white/60 uppercase tracking-wider mt-1">Machines</p>
                  </div>
                  <div>
                    <p className="font-serif text-2xl md:text-3xl font-medium text-white">21</p>
                    <p className="font-sans text-xs text-white/60 uppercase tracking-wider mt-1">Sewing Lines</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Highlights Grid */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
          className="mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {highlights.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group p-8 bg-slate-50 hover:bg-white border border-transparent hover:border-slate-200 transition-all duration-500 hover:shadow-xl hover:shadow-slate-200/50"
            >
              <item.icon 
                size={32} 
                className="text-rose-700 mb-4" 
                strokeWidth={1.5} 
              />
              <h3 className="font-serif text-xl text-slate-900 mb-2">
                {item.title}
              </h3>
              <p className="font-sans text-sm text-slate-600 leading-relaxed">
                {item.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
